import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Search, AlertCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuppliersReport = () => {
  const [report, setReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    if (report && report.suppliers) {
      if (searchTerm) {
        const filtered = report.suppliers.filter(supplier =>
          supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          supplier.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredSuppliers(filtered);
      } else {
        setFilteredSuppliers(report.suppliers);
      }
    }
  }, [searchTerm, report]);

  const fetchReport = async () => {
    try {
      const response = await apiClient.get('/api/inventory/reports/suppliers');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setReport(data);
      setFilteredSuppliers(data.suppliers || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (!report) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Total Suppliers</div>
            <div className="text-3xl font-bold">{report.total_suppliers}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              Total Due Amount
            </div>
            <div className="text-3xl font-bold text-red-600">₹{report.total_due_amount?.toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Suppliers With Due</div>
            <div className="text-3xl font-bold text-orange-600">{report.suppliers_with_due}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Suppliers Report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Supplier Name</th>
                  <th className="text-left p-3">Company</th>
                  <th className="text-left p-3">Phone</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">GST Number</th>
                  <th className="text-left p-3">Balance Due</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.length === 0 ? (
                  <tr><td colSpan="7" className="text-center p-4 text-gray-500">No suppliers found.</td></tr>
                ) : (
                  filteredSuppliers.map((supplier) => {
                    const hasDue = supplier.balance > 0;
                    return (
                      <tr key={supplier.id} className={`border-b hover:bg-gray-50 ${hasDue ? 'bg-red-50' : ''}`}>
                        <td className="p-3 font-medium">{supplier.name}</td>
                        <td className="p-3">{supplier.company_name || 'N/A'}</td>
                        <td className="p-3">{supplier.phone}</td>
                        <td className="p-3">{supplier.email || 'N/A'}</td>
                        <td className="p-3">{supplier.gst_number || 'N/A'}</td>
                        <td className="p-3">
                          <span className={`font-bold ${hasDue ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{supplier.balance}
                          </span>
                        </td>
                        <td className="p-3">
                          {hasDue ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                              <AlertCircle className="w-3 h-3" />
                              Payment Due
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Paid</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuppliersReport;